import { Injectable, signal } from '@angular/core';
import { FormArray } from '@angular/forms';
import {
  NPSAreasEnum,
  CarouselType,
  CarouselTypeResult,
  ChoiceTypeResult,
  Command,
  Condition,
  QuestionCard,
  QuestionOption,
  QuestionType,
  RangeOperators,
  RatingType,
  SurveyResult
} from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { QuestionControl } from '../models/page-control.interface';

@Injectable({ providedIn: 'root' })
export class SurveyConditionalService {
  private questions: { [questionId: string]: Question } = {};
  private questionControls: QuestionControl = {};
  private mappedTriggerAndAffectedQuestions: { [triggerQId: string]: string[] } = {};
  private mappedTriggerQuestionAndAffectedCards: { [triggerQId: string]: { [qId: string]: string }[] } = {};
  private mappedTriggerQuestionAndAffectedOptions: { [triggerQId: string]: { [qId: string]: string }[] } = {};
  private skipControlsReset$ = signal(false);

  mapConditionality(question: Question, mappedQuestions: { [questionId: string]: Question }, questionControls: QuestionControl): void {
    this.questions = mappedQuestions;
    this.questionControls = questionControls;

    this.mapQuestionConditionality(question);
    if (question.options?.length > 0) {
      question.options.forEach((option) => this.mapQuestionOptionConditionality(question.id, option));
    }
    if (question.cards?.length > 0) {
      question.cards.forEach((card) => this.mapQuestionCardConditionality(question.id, card));
    }
  }

  runSurveyConditionality(questions: Question[], answers: SurveyResult): void {
    this.questions = {};
    this.questionControls = {};
    this.mappedTriggerAndAffectedQuestions = {};
    this.mappedTriggerQuestionAndAffectedCards = {};
    this.mappedTriggerQuestionAndAffectedOptions = {};
    this.skipControlsReset$.set(true);

    questions.forEach((question) => {
      this.questions[question.id] = question;
      const qControl = question.getFormControl();
      if (answers[question.id]) {
        qControl.patchValue(JSON.parse(answers[question.id] as string));
      }
      this.questionControls[question.id] = qControl;
      this.mapQuestionConditionality(question);
    });

    questions.forEach((question) => this.run(question.id));
  }

  run(questionId: string): void {
    this.runConditionality(this.mappedTriggerAndAffectedQuestions[questionId]);
    this.runOptionConditionality(this.mappedTriggerQuestionAndAffectedOptions[questionId]);
    this.runCardConditionality(this.mappedTriggerQuestionAndAffectedCards[questionId]);
  }

  destroy(): void {
    this.questions = {};
    this.questionControls = {};
    this.mappedTriggerAndAffectedQuestions = {};
    this.mappedTriggerQuestionAndAffectedOptions = {};
    this.mappedTriggerQuestionAndAffectedCards = {};
  }

  private runConditionality(affectedQuestionIds: string[]): void {
    if (!affectedQuestionIds || affectedQuestionIds.length === 0) {
      return;
    }

    affectedQuestionIds.forEach((qId) => {
      const question = this.questions[qId];
      question.visible = this.checkSuccessCondition(question.visibleWhen);

      const qControl = this.questionControls[qId];

      if (CarouselType.includes(question.type)) {
        if (!question.visible && !this.skipControlsReset$()) {
          qControl.reset();
        }
        return;
      }

      if (!question.visible) {
        qControl.disable();
        if (!this.skipControlsReset$()) {
          qControl.reset();
        }
      } else {
        qControl.enable();
      }
    });
  }

  private runOptionConditionality(affectedQuestionOptionIds: { [qId: string]: string }[]): void {
    if (!affectedQuestionOptionIds || affectedQuestionOptionIds.length === 0) {
      return;
    }

    affectedQuestionOptionIds.forEach((qustionOptionRecord) => {
      const qId = Object.keys(qustionOptionRecord)[0];
      const optionId = Object.values(qustionOptionRecord)[0];

      const question = this.questions[qId];
      const option = question.options.find((opt) => opt.id === optionId);
      option.visible = this.checkSuccessCondition(option.visibleWhen);

      if (!this.skipControlsReset$()) {
        this.questionControls[qId].reset();
      }
    });
  }

  private runCardConditionality(affectedQuestionCardIds: { [qId: string]: string }[]): void {
    if (!affectedQuestionCardIds || affectedQuestionCardIds.length === 0) {
      return;
    }

    affectedQuestionCardIds.forEach((qustionCardRecord) => {
      const qId = Object.keys(qustionCardRecord)[0];
      const cardId = Object.values(qustionCardRecord)[0];

      const question = this.questions[qId];
      const card = question.cards.find((c) => c.id === cardId);
      card.visible = this.checkSuccessCondition(card.visibleWhen);

      if (!card.visible) {
        (this.questionControls[qId] as FormArray).controls[card.id].disable();
      } else {
        (this.questionControls[qId] as FormArray).controls[card.id].enable();
      }
      if (!this.skipControlsReset$()) {
        this.questionControls[qId].reset();
      }
    });
  }

  private mapQuestionConditionality(question: Question): void {
    if (!question.visibleWhen || question.visibleWhen.length === 0) {
      // All questions not affected by a condition are visible by default
      question.visible = true;
      this.questionControls[question.id]?.enable();
      return;
    }

    question.visibleWhen
      .filter((condition) => typeof condition === 'object')
      .forEach((condition: Condition) => {
        this.mappedTriggerAndAffectedQuestions[condition.questionId] = this.mappedTriggerAndAffectedQuestions[condition.questionId] || [];
        this.mappedTriggerAndAffectedQuestions[condition.questionId].push(question.id);
      });
  }

  private mapQuestionOptionConditionality(questionId: string, option: QuestionOption): void {
    if (!option.visibleWhen || option.visibleWhen.length === 0) {
      // All options not affected by a condition are visible by default
      option.visible = true;
      return;
    }

    option.visibleWhen
      .filter((condition) => typeof condition === 'object')
      .forEach((condition: Condition) => {
        const affectedQuestionOption = { [questionId]: option.id };
        this.mappedTriggerQuestionAndAffectedOptions[condition.questionId] = this.mappedTriggerQuestionAndAffectedOptions[condition.questionId] || [];
        this.mappedTriggerQuestionAndAffectedOptions[condition.questionId].push(affectedQuestionOption);
      });
  }

  private mapQuestionCardConditionality(questionId: string, card: QuestionCard): void {
    if (!card.visibleWhen || card.visibleWhen.length === 0) {
      // All cards not affected by a condition are visible by default
      card.visible = true;
      return;
    }

    card.visibleWhen
      .filter((condition) => typeof condition === 'object')
      .forEach((condition: Condition) => {
        const affectedQuestionCard = { [questionId]: card.id };
        this.mappedTriggerQuestionAndAffectedCards[condition.questionId] = this.mappedTriggerQuestionAndAffectedCards[condition.questionId] || [];
        this.mappedTriggerQuestionAndAffectedCards[condition.questionId].push(affectedQuestionCard);
      });
  }

  private checkSuccessCondition(conditions: (Condition | Command)[]): boolean {
    // Set group for all conditions without it.
    conditions
      .filter((condition) => typeof condition === 'object')
      .filter((condition: Condition) => condition.group === undefined || condition.group === null)
      .forEach((condition: Condition) => (condition.group = 1));

    const groupedConditions = this.mapConditionGroups(conditions);

    const resolvedConditionsMap = [];
    groupedConditions.forEach((group) => {
      const queryParts1 = this.mapConditionQueryParts(group);
      const resolution = queryParts1.some((queryPart) => queryPart.every((condition) => this.checkCondition(condition)));

      resolvedConditionsMap.push(resolution);
      if (typeof group.slice(-1)[0] !== 'object') {
        resolvedConditionsMap.push(group.slice(-1)[0]);
      }
    });

    const resolvedParts = this.mapResolvedGroups(resolvedConditionsMap);
    return resolvedParts.some((part) => part.every((condition) => condition));
  }

  private mapConditionGroups(conditions: (Condition | Command)[]): Condition[][] {
    const groupOfConditions: Condition[][] = [];
    let group = (conditions[0] as Condition).group;
    groupOfConditions[group] = [];
    conditions.forEach((condition) => {
      if (typeof condition === 'object' && (condition as Condition).group !== group) {
        group = (condition as Condition).group;
        groupOfConditions[group] = [];
      }
      groupOfConditions[group].push(condition as Condition);
    });

    return groupOfConditions;
  }

  private mapResolvedGroups(conditions: (boolean | Command)[]): boolean[][] {
    const conditionQueryParts: boolean[][] = [];
    let groupIndex = 0;
    conditionQueryParts[groupIndex] = [];

    conditions.forEach((condition) => {
      if (condition === 'OR') {
        groupIndex++;
        conditionQueryParts[groupIndex] = [];
      } else {
        conditionQueryParts[groupIndex].push(condition as boolean);
      }
    });

    return conditionQueryParts;
  }

  private mapConditionQueryParts(conditions: (Condition | Command)[]): Condition[][] {
    const conditionQueryParts: Condition[][] = [];
    let groupIndex = 0;
    conditionQueryParts[groupIndex] = [];

    conditions.forEach((condition) => {
      if (typeof condition === 'object') {
        conditionQueryParts[groupIndex].push(condition as Condition);
      } else if (condition === 'OR') {
        groupIndex++;
        conditionQueryParts[groupIndex] = [];
      }
    });

    return conditionQueryParts;
  }

  private checkCondition(condition: Condition): boolean {
    const valueToCheck = this.questionControls[condition.questionId].value;

    if (condition.cardId) {
      return this.checkCarouselCondition(condition, valueToCheck);
    } else if (condition.optionId) {
      return this.checkRankingCondition(condition, valueToCheck);
    } else {
      if (typeof valueToCheck === 'object') {
        return this.checkIfConditionApplies(condition, valueToCheck.value);
      } else {
        return this.checkIfConditionApplies(condition, valueToCheck);
      }
    }
  }

  private checkCarouselCondition(condition: Condition, carouselAnswer: CarouselTypeResult): boolean {
    const triggerQuestion = this.questions[condition.questionId];
    let plainValueToMatch: string;
    let mappedValuesToCheckFromIds: string[] = [];

    if (condition.valueToMatch) {
      if (triggerQuestion.options?.length > 0) {
        mappedValuesToCheckFromIds = triggerQuestion.options.filter((option) => condition.valueToMatch.includes(option.id)).map((option) => option.value);
        if (condition.valueToMatch.includes('OTHER')) {
          mappedValuesToCheckFromIds = [...mappedValuesToCheckFromIds, 'OTHER'];
        }
        plainValueToMatch = mappedValuesToCheckFromIds[0];
      } else {
        plainValueToMatch = condition.valueToMatch[0];
      }
    }

    if (!carouselAnswer[condition.cardId]) {
      return false;
    }

    const valueToCheck = carouselAnswer[condition.cardId].value;

    switch (condition.operator) {
      case 'EQUAL':
        return plainValueToMatch === valueToCheck;
      case 'NOT_EQUAL':
        return plainValueToMatch !== valueToCheck;
      case 'EQUAL_ANY':
        return mappedValuesToCheckFromIds.includes(valueToCheck as string);
      case 'NOT_EMPTY':
        return valueToCheck?.length > 0;
      case 'INCLUDES_ALL':
        return mappedValuesToCheckFromIds.every((value) => valueToCheck?.includes(value));
      case 'INCLUDES_ANY':
        return mappedValuesToCheckFromIds.some((value) => valueToCheck?.includes(value));
      case 'NOT_INCLUDES':
        return !mappedValuesToCheckFromIds.some((value) => valueToCheck?.includes(value));
      case 'NOT_INCLUDES_ALL':
        return !mappedValuesToCheckFromIds.every((value) => valueToCheck?.includes(value));
      case 'LESS_THAN':
        return +plainValueToMatch > +valueToCheck;
      case 'LESS_THAN_OR_EQUAL':
        return +plainValueToMatch >= +valueToCheck;
      case 'GREATER_THAN':
        return +plainValueToMatch < +valueToCheck;
      case 'GREATER_THAN_OR_EQUAL':
        return +plainValueToMatch <= +valueToCheck;
      case 'ONLY_IF': {
        return this.arraysHaveSameValues(mappedValuesToCheckFromIds, [...valueToCheck]);
      }
      default:
        return false;
    }
  }

  private checkIfConditionApplies(condition: Condition, valueToCheck: string): boolean {
    const triggerQuestion = this.questions[condition.questionId];
    let plainValueToMatch: string;
    let mappedValuesToCheckFromIds: string[] = [];

    if (condition.valueToMatch) {
      if (triggerQuestion.options?.length > 0) {
        mappedValuesToCheckFromIds = triggerQuestion.options.filter((option) => condition.valueToMatch.includes(option.id)).map((option) => option.value);
        if (condition.valueToMatch.includes('OTHER')) {
          mappedValuesToCheckFromIds = [...mappedValuesToCheckFromIds, 'OTHER'];
        }
        plainValueToMatch = mappedValuesToCheckFromIds[0];
      } else {
        plainValueToMatch = condition.valueToMatch[0];
      }
    }

    switch (condition.operator) {
      case 'EQUAL':
        return plainValueToMatch === valueToCheck;
      case 'NOT_EQUAL':
        return plainValueToMatch !== valueToCheck;
      case 'EQUAL_ANY':
        return mappedValuesToCheckFromIds.includes(valueToCheck as string);
      case 'NOT_EMPTY':
        return valueToCheck?.length > 0;
      case 'LESS_THAN':
        return +plainValueToMatch > +valueToCheck;
      case 'LESS_THAN_OR_EQUAL':
        return +plainValueToMatch >= +valueToCheck;
      case 'GREATER_THAN':
        return +plainValueToMatch < +valueToCheck;
      case 'GREATER_THAN_OR_EQUAL':
        return +plainValueToMatch <= +valueToCheck;
      case 'INCLUDES_ALL':
        return mappedValuesToCheckFromIds.every((value) => valueToCheck?.includes(value));
      case 'INCLUDES_ANY':
        return mappedValuesToCheckFromIds.some((value) => valueToCheck?.includes(value));
      case 'NOT_INCLUDES':
        return !mappedValuesToCheckFromIds.some((value) => valueToCheck?.includes(value));
      case 'NOT_INCLUDES_ALL':
        return !mappedValuesToCheckFromIds.every((value) => valueToCheck?.includes(value));
      case 'CONTAINS':
        return condition.valueToMatch.every((value) => valueToCheck.trim().toLowerCase().includes(value.trim().toLowerCase()));
      case 'NOT_CONTAINS':
        return !condition.valueToMatch.some((value) => valueToCheck.trim().toLowerCase().includes(value.trim().toLowerCase()));
      case 'NPS_SCORE': {
        const areaToMatch = +valueToCheck < 7 ? NPSAreasEnum.Detractors : +valueToCheck > 8 ? NPSAreasEnum.Promoters : NPSAreasEnum.Passives;
        return condition.valueToMatch.includes(areaToMatch);
      }
      case 'ONLY_IF': {
        return this.arraysHaveSameValues(mappedValuesToCheckFromIds, [...valueToCheck]);
      }
      default:
        return false;
    }
  }

  private checkRankingCondition(condition: Condition, valueToCheck: string[] | ChoiceTypeResult): boolean {
    const triggerQuestion = this.questions[condition.questionId];
    const answerValues = (triggerQuestion.type === 'rankSort' ? (valueToCheck as ChoiceTypeResult).value : valueToCheck) as string[];
    const isRankSortDescriptive = triggerQuestion.type === QuestionType.RankSort && triggerQuestion.ratingType === RatingType.descriptive;
    const optionValue = condition.optionId === 'NONE' ? 'NONE' : triggerQuestion.options.find((option) => option.id === condition.optionId)?.value;
    const valuePosition = answerValues.indexOf(optionValue) + 1;
    const plainValueToMatch = isRankSortDescriptive
      ? triggerQuestion.ranks.filter((r) => condition.valueToMatch.includes(r.id)).map((option) => option.value)
      : condition.valueToMatch;

    if (!optionValue || (RangeOperators.includes(condition.operator) && plainValueToMatch.length !== 2)) {
      return false;
    }

    switch (condition.operator) {
      case 'POSITION_BETWEEN':
        return +plainValueToMatch[0] <= valuePosition && +plainValueToMatch[1] >= valuePosition;
      case 'POSITION_GREATER_THAN':
        return +plainValueToMatch[0] > valuePosition;
      case 'POSITION_GREATER_THAN_OR_EQUAL':
        return +plainValueToMatch[0] >= valuePosition;
      case 'EQUAL':
      case 'POSITION_EQUAL':
        return +plainValueToMatch[0] === valuePosition;
      case 'EQUAL_ANY':
      case 'POSITION_INCLUDES_ANY':
        return plainValueToMatch.some((v) => +v === valuePosition);
      case 'POSITION_NOT_BETWEEN':
        return !(+plainValueToMatch[0] <= valuePosition && +plainValueToMatch[1] >= valuePosition);
      case 'NOT_EQUAL':
      case 'POSITION_NOT_EQUAL':
        return +plainValueToMatch[0] !== valuePosition;
      case 'POSITION_SMALLER_THAN':
        return +plainValueToMatch[0] < +valuePosition;
      case 'POSITION_SMALLER_THAN_OR_EQUAL':
        return +plainValueToMatch[0] <= +valuePosition;
      case 'NOT_SELECTED':
        return !answerValues.includes(optionValue);
      case 'SELECTED':
        return answerValues.includes(optionValue);
      default:
        return false;
    }
  }

  private arraysHaveSameValues(first: string[], second: string[]): boolean {
    const firstSet = new Set(first);
    const secondSet = new Set(second);
    if (firstSet.size !== secondSet.size) {
      return false;
    }
    return [...firstSet].every((value) => secondSet.has(value));
  }
}
